import { useEffect, useRef } from "react";
import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiText,
  EuiSpacer,
  EuiButtonIcon,
  EuiToolTip,
  EuiBadge,
} from "@elastic/eui";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { OpsanioLocationValue } from "src/questionnaires";
import { useAnketaI18n } from "src/internationalization";
import {
  TILE_ATTRIBUTION,
  TILE_LAYER_URL,
  hasCoordinates,
  useMapTiles,
} from "src/helper/map";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

interface ApplianceLocationMapProps {
  location: OpsanioLocationValue;
  height?: number;
}

export const ApplianceLocationMap = ({
  location,
  height = 220,
}: ApplianceLocationMapProps): JSX.Element => {
  const { translate } = useAnketaI18n();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);

  const tiles = useMapTiles();
  const hasCoords = hasCoordinates(location);
  // No map at all when the site has no coordinates, or when the tile server
  // refuses to serve us (403) - an empty grey grid is worse than nothing.
  const showMap = hasCoords && tiles.status === "available";

  useEffect(() => {
    if (!showMap || !mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      center: [location.lat, location.lng],
      zoom: 13,
      zoomControl: true,
      scrollWheelZoom: false,
    });

    L.tileLayer(TILE_LAYER_URL, {
      attribution: TILE_ATTRIBUTION,
      maxZoom: 19,
    }).addTo(map);

    L.marker([location.lat, location.lng], {
      icon: L.divIcon({
        className: "",
        html: `<div style="
          background: #006BB4;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          border: 2px solid white;
          box-shadow: 0 1px 4px rgba(0,0,0,0.4);
        "></div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      }),
    })
      .addTo(map)
      .bindPopup(
        `<strong>${location.city ?? location.displayName.split(",")[0]}</strong>` +
          (location.country ? `<br/>${location.country}` : ""),
        { autoClose: false, closeOnClick: false },
      )
      .openPopup();

    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, [location.lat, location.lng, showMap]);

  const openGoogleMaps = () =>
    window.open(
      `https://www.google.com/maps?q=${location.lat},${location.lng}`,
      "_blank",
    );

  const openAppleMaps = () => {
    const label = encodeURIComponent(
      location.city ?? location.displayName.split(",")[0],
    );
    window.open(
      `https://maps.apple.com/?ll=${location.lat},${location.lng}&q=${label}`,
      "_blank",
    );
  };

  const addressLines = [
    location.street,
    [location.postalCode, location.city].filter(Boolean).join(" "),
    location.country,
  ].filter(Boolean);

  return (
    <>
      <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiIcon type="mapMarker" color="#006BB4" size="m" />
        </EuiFlexItem>
        <EuiFlexItem>
          {addressLines.length > 0 ? (
            addressLines.map((line, i) => (
              <EuiText
                key={i}
                size={i === 0 ? "s" : "xs"}
                color={i === 0 ? undefined : "subdued"}
              >
                {line}
              </EuiText>
            ))
          ) : (
            <EuiText size="s" color="subdued">
              {location.displayName}
            </EuiText>
          )}
        </EuiFlexItem>

        {location.isPrimary && (
          <EuiFlexItem grow={false}>
            <EuiToolTip
              content={translate(
                "anketa.opsanio.appliance.flyout.location.primary.tooltip",
                { defaultMessage: "Marked as primary location" },
              )}
            >
              <EuiBadge color="primary">
                {translate(
                  "anketa.opsanio.appliance.flyout.location.primary.badge",
                  { defaultMessage: "Primary location" },
                )}
              </EuiBadge>
            </EuiToolTip>
          </EuiFlexItem>
        )}

        <EuiFlexItem grow={false} hidden={!hasCoords}>
          <EuiFlexGroup gutterSize="xs" responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiToolTip
                content={translate(
                  "anketa.opsanio.appliance.flyout.location.openGoogleMaps",
                  { defaultMessage: "Open in Google Maps" },
                )}
              >
                <EuiButtonIcon
                  aria-label={translate(
                    "anketa.opsanio.appliance.flyout.location.openGoogleMaps",
                    { defaultMessage: "Open in Google Maps" },
                  )}
                  iconType="globe"
                  size="xs"
                  color="primary"
                  onClick={openGoogleMaps}
                />
              </EuiToolTip>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiToolTip
                content={translate(
                  "anketa.opsanio.appliance.flyout.location.openAppleMaps",
                  { defaultMessage: "Open in Apple Maps" },
                )}
              >
                <EuiButtonIcon
                  aria-label={translate(
                    "anketa.opsanio.appliance.flyout.location.openAppleMaps",
                    { defaultMessage: "Open in Apple Maps" },
                  )}
                  iconType="visMapRegion"
                  size="xs"
                  color="text"
                  onClick={openAppleMaps}
                />
              </EuiToolTip>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer size="s" />

      {showMap && (
        <div
          ref={mapRef}
          style={{
            height,
            width: "100%",
            borderRadius: 6,
            overflow: "hidden",
            border: "1px solid #D3DAE6",
          }}
        />
      )}

      {!hasCoords && (
        <EuiText size="xs" color="subdued">
          {translate("anketa.opsanio.appliance.flyout.location.noCoordinates", {
            defaultMessage:
              "This address was entered manually and has no coordinates.",
          })}
        </EuiText>
      )}

      {hasCoords && tiles.status === "blocked" && (
        <EuiText size="xs" color="subdued">
          {translate("anketa.opsanio.appliance.flyout.location.mapUnavailable", {
            values: { status: tiles.httpStatus ?? "" },
            defaultMessage:
              "Map view unavailable - the tile server refuses to deliver map tiles {{status}}.",
          })}
        </EuiText>
      )}

      {(location.comment || location.weitereInfos || location.breakouts) && (
        <>
          <EuiSpacer size="s" />
          {location.comment && (
            <EuiText size="xs">
              <strong>
                {translate(
                  "anketa.opsanio.appliance.flyout.location.comment.label",
                  { defaultMessage: "Comment:" },
                )}
              </strong>{" "}
              {location.comment}
            </EuiText>
          )}
          {location.weitereInfos && (
            <EuiText size="xs">
              <strong>
                {translate(
                  "anketa.opsanio.appliance.flyout.location.moreInfo.label",
                  { defaultMessage: "Additional location information:" },
                )}
              </strong>{" "}
              {location.weitereInfos}
            </EuiText>
          )}
          {location.breakouts && (
            <EuiText size="xs">
              <strong>
                {translate(
                  "anketa.opsanio.appliance.flyout.location.breakouts.label",
                  { defaultMessage: "Network breakouts:" },
                )}
              </strong>{" "}
              {location.breakouts}
            </EuiText>
          )}
        </>
      )}
    </>
  );
};
